// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { TextInput, type TextInputProps } from '@mantine/core';

import { convertDurationToSeconds, convertSecondsToDuration, isDuration } from 'components/Mantine/durationHelpers';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { isNotNil } from 'helpers/isNotNil';

type Props = {
  /** number of seconds of total duration */
  defaultValue?: number;
  onChange: (value: number) => void;
  /** called to alert the parent if the input is valid or not */
  onValidChange?: (validValue: boolean) => void;
  /** Maximum allowed value in seconds */
  max?: number;
} & StrongOmit<TextInputProps, 'value' | 'defaultValue' | 'onChange' | 'error'>;

const durationAsNumberRegex = /^\d*(\.\d+)?$/;

export const durationIsValid = (duration: string, max: number) =>
  (isDuration(duration) && convertDurationToSeconds(duration) <= max) ||
  duration === '' ||
  // Duration is in hours, ie. 2.5
  (durationAsNumberRegex.test(duration) && parseFloat(duration) <= max / 3_600);

const parseDuration = (duration: string, max: number) => {
  const isValid = durationIsValid(duration, max);
  if (!isValid) return null;
  return durationAsNumberRegex.test(duration)
    ? Math.min(Math.floor(parseFloat(duration || '0') * 3_600), max)
    : isDuration(duration)
      ? convertDurationToSeconds(duration)
      : 0;
};

/**
 * Used to collect a number of hours and seconds from a user
 */
export const DurationInput = ({
  defaultValue,
  onChange,
  onValidChange,
  // 999:59 as seconds
  max = 3_599_940,
  ...props
}: Props) => {
  const [value, setValue] = useState<string>(
    defaultValue && defaultValue > 0 ? convertSecondsToDuration(defaultValue) : '',
  );

  const onSubmit = (newValue: string) => {
    setValue(newValue);
    const parsedValue = parseDuration(newValue, max);
    onValidChange?.(isNotNil(parsedValue));
    if (isNotNil(parsedValue)) {
      onChange(parsedValue);
    }
  };

  return (
    <TextInput
      w={120}
      styles={{
        input: {
          textAlign: 'right',
          fontSize: 16,
        },
      }}
      leftSection={
        <InfoIcon
          color={durationIsValid(value, max) ? undefined : 'red'}
          label={
            <>
              Allowed formats:
              <ul style={{ margin: 0, textAlign: 'left' }}>
                <li>
                  <code>hh:mm</code> (
                  <b>
                    <code>02:30</code>
                  </b>
                  )
                </li>
                <li>
                  total hours (eg{' '}
                  <b>
                    <code>2.5</code>
                  </b>
                  )
                </li>
              </ul>
            </>
          }
        />
      }
      maxLength={10}
      autoFocus
      onFocus={(e) => e.currentTarget.setSelectionRange(0, e.currentTarget.value.indexOf(':'))}
      onKeyDown={(e) => {
        const v = e.currentTarget.value;
        const colonPosition = v.indexOf(':');
        const cursorPosition = e.currentTarget.selectionStart ?? colonPosition;
        if (e.key === 'Tab') {
          if (colonPosition > 0) {
            if (colonPosition >= cursorPosition) {
              e.currentTarget.setSelectionRange(colonPosition + 1, v.length);
            } else {
              e.currentTarget.setSelectionRange(0, colonPosition);
            }
          }
          e.preventDefault();
        }
      }}
      placeholder="hh:mm"
      {...props}
      onBlur={(e) => {
        setValue((v) => {
          const parsedValue = parseDuration(value, max);
          return isNotNil(parsedValue) ? convertSecondsToDuration(parsedValue) : v;
        });
        props.onBlur?.(e);
      }}
      error={!durationIsValid(value, max)}
      value={value}
      onChange={(event) => onSubmit(event.currentTarget.value)}
    />
  );
};
